/**
 * Cosmo Crypt - Page Title Manager
 * Automatically updates browser tab titles dynamically based on active route and template.
 */

const DEFAULT_TITLE = 'Cosmo Crypt – Zero-Knowledge Gateway & Credentials Registry';

export const TITLE_MAP = {
  // A. Core Portal & Operations
  "about.html": "Cosmo Crypt – Zero-Knowledge Gateway & Credentials Registry",
  "account.html": "Secure Account Portal — Identity & Enclave Access | Cosmo Crypt",
  "console.html": "Security Console — Command Center & Vault Switcher | Cosmo Crypt",
  "preferences.html": "Security Preferences — Biometrics & App Lock | Cosmo Crypt",
  "notif.html": "Security Alerts — Live Telemetry & Audit Logs | Cosmo Crypt",
  "sessions.html": "Active Sessions — Device Control & Remote Revocation | Cosmo Crypt",
  "analytics.html": "Vault Analytics — Telemetry & Cryptographic Metrics | Cosmo Crypt",
  "feedback.html": "Feedback & Bug Bounty — Security Telemetry | Cosmo Crypt",
  "knowledge.html": "Research Hub — Cryptography Papers & Fusion Forge | Cosmo Crypt",
  "legal.html": "Legal & Privacy — Zero-Knowledge Commitments | Cosmo Crypt",
  "upgrade.html": "Premium Vault Plans — Unlock Elite Enclave Tiers | Cosmo Crypt",
  "upgrade-features.html": "Free vs Premium — Full Enclave Feature Comparison | Cosmo Crypt",

  // B. Vaults & Credentials Registries
  "developer-endpoints.html": "API Key Vault — Tokens, Webhooks & Endpoints | Cosmo Crypt",
  "financial-credentials.html": "Financial Vault — Encrypted Banking, IBAN & Ledgers | Cosmo Crypt",
  "payment-gateways.html": "Payment Gateway Vault — Stripe, PayPal & UPI | Cosmo Crypt",
  "merchant-identities.html": "Merchant Identity Vault — OAuth & App Secrets | Cosmo Crypt",
  "national-identity.html": "National ID Vault — Passports & Encrypted KYC | Cosmo Crypt",
  "identity-vault.html": "Identity Vault — Master Credential Registry | Cosmo Crypt",
  "encrypted-partitions.html": "Encrypted Partitions — Zero-Knowledge Secure Notes | Cosmo Crypt",
  "dns-management.html": "Secure DNS Management — Domains & DNSSEC | Cosmo Crypt",
  "node-infrastructure.html": "Node Infrastructure — Gateway & Enclave Clusters | Cosmo Crypt",
  "workspace-integration.html": "Workspace Vault — Google SSO & Cloud Recovery Keys | Cosmo Crypt",

  // C. Cryptography & Security Protocols
  "shamir-secret-sharing.html": "Shamir's Secret Sharing — Threshold Key Recovery | Cosmo Crypt",
  "security-architecture.html": "Security Architecture — Zero-Knowledge Enclaves | Cosmo Crypt",
  "security-2fa.html": "Two-Factor Authentication — TOTP & FIDO2 Passkeys | Cosmo Crypt",
  "system-encryption.html": "System Encryption — Hardware & Partition Locks | Cosmo Crypt",
  "documentation.html": "Documentation — Handbooks, Manuals & Blueprints | Cosmo Crypt",

  // D. Technical Comparisons (Brand at front — clean SERP snippet)
  "vs-1password.html": "Cosmo Crypt vs 1Password — Zero-Knowledge Architecture Compared",
  "vs-bitwarden.html": "Cosmo Crypt vs Bitwarden — Zero-Knowledge Benchmark Compared",

  // E. Terminology & Protocol Dictionaries (Optimized for Search Glossaries)
  "api-terminology.html": "API Security Glossary — Tokens, Keys & Endpoints | Cosmo Crypt",
  "app-terminology.html": "App Security Glossary — Sandboxing & Tokenization | Cosmo Crypt",
  "banking-terminology.html": "Banking Crypto Glossary — Protocols & Ledgers | Cosmo Crypt",
  "dns-terminology.html": "DNS Security Glossary — DNSSEC & Zone Signing | Cosmo Crypt",
  "encryption-terminology.html": "Encryption Glossary — AES-256-GCM, PBKDF2 & RSA | Cosmo Crypt",
  "google-terminology.html": "Enterprise Identity Glossary — OAuth2 & SSO | Cosmo Crypt",
  "national-terminology.html": "Sovereign Identity Glossary — Decentralized KYC | Cosmo Crypt",
  "payment-terminology.html": "Payment Security Glossary — Gateways, UPI & PSPs | Cosmo Crypt"
};

// Cosmo Crypt internal slug aliases (used by navigation cards & switchView)
const ALIAS_MAP = {
  'merchant': 'merchant-identities',
  'apps': 'merchant-identities',
  'merchant-identities': 'merchant-identities',
  'domain': 'dns-management',
  'dns': 'dns-management',
  'dns-management': 'dns-management',
  'league': 'node-infrastructure',
  'node': 'node-infrastructure',
  'node-infrastructure': 'node-infrastructure',
  'banks': 'financial-credentials',
  'financial': 'financial-credentials',
  'financial-credentials': 'financial-credentials',
  'payment': 'payment-gateways',
  'payment-gateways': 'payment-gateways',
  'api-keys': 'developer-endpoints',
  'developer-endpoints': 'developer-endpoints',
  'settings': 'preferences',
  'setting': 'preferences',
  'preferences': 'preferences',
  'privacy': 'legal',
  'legal': 'legal',
  'faq': 'knowledge',
  'knowledge': 'knowledge',
  'why-upgrade': 'upgrade-features',
  'why-we-upgrade': 'upgrade-features',
  'upgrade-features': 'upgrade-features',
  'insight': 'analytics',
  'analysis': 'analytics',
  'analytics': 'analytics',
  'plans': 'upgrade',
  'subscription': 'upgrade',
  'upgrade': 'upgrade',
  'pro-tier': 'upgrade',
  'login': 'account',
  'account': 'account',
  'generator': 'identity-vault',
  'document-repository': 'identity-vault',
  'vault': 'identity-vault',
  'identity-vault': 'identity-vault',
  'decoder': 'system-encryption',
  'credential': 'system-encryption',
  'locks': 'system-encryption',
  'system-encryption': 'system-encryption',
  'barcode-creator': 'system-encryption',
  'barcode': 'system-encryption',
  'barcode-decoder': 'financial-credentials',
  'google': 'workspace-integration',
  'workspace-integration': 'workspace-integration',
  'national': 'national-identity',
  'national-identity': 'national-identity',
  'notebook': 'encrypted-partitions',
  'encrypted-notebook': 'encrypted-partitions',
  'encrypted-partitions': 'encrypted-partitions',
  'apps-terminology': 'app-terminology',
  'domain-terminology': 'dns-terminology',
  'locks-terminology': 'encryption-terminology',
  'totp': 'security-2fa',
  '2fa': 'security-2fa',
  'two-factor': 'security-2fa',
  'security-2fa': 'security-2fa',
  'architecture': 'security-architecture',
  'passkeys': 'security-architecture',
  'duress': 'security-architecture',
  'panic-mode': 'security-architecture',
  'security-architecture': 'security-architecture',
  'alerts': 'notif',
  'notif': 'notif',
  'shamir': 'shamir-secret-sharing',
  'sss': 'shamir-secret-sharing',
  'shamir-secret-sharing': 'shamir-secret-sharing',
  'devices': 'sessions',
  'active-sessions': 'sessions',
  'active-devices': 'sessions',
  'sessions': 'sessions',
  '1password': 'vs-1password',
  'vs-1password': 'vs-1password',
  'bitwarden': 'vs-bitwarden',
  'vs-bitwarden': 'vs-bitwarden'
};

/**
 * Safely extracts a clean route slug from any URL, path, hash, or state object.
 * Fixes edge cases:
 * - Query strings ('?tab=1') & hashes ('#preferences')
 * - Full URLs ('https://cosmocrypt.com/console')
 * - Suffixes ('.html', '-template')
 */
export function extractSlug(rawInput) {
  if (!rawInput) return '';
  let str = String(rawInput).trim();

  // Handle absolute URLs (e.g. https://cosmocrypt.com/console?tab=1)
  if (str.startsWith('http://') || str.startsWith('https://')) {
    try {
      str = new URL(str).pathname;
    } catch (e) {
      str = str.replace(/^https?:\/\/[^\/]+/, '');
    }
  }

  // Handle leading hash anchors (e.g. '#preferences' -> 'preferences')
  str = str.replace(/^#+/, '');

  // Strip query parameters (?...) and hash fragments (#...)
  str = str.split('?')[0].split('#')[0];

  // Strip leading and trailing slashes
  str = str.replace(/^\/+|\/+$/g, '');

  // Extract first path segment if multi-path (e.g. 'console/settings' -> 'console')
  if (str.includes('/')) {
    str = str.split('/')[0];
  }

  // Strip '.html' and '-template' extensions
  str = str.replace(/\.html$/i, '').replace(/-template$/i, '');

  return str.toLowerCase().trim();
}

/**
 * Resolves the target title from slug/path/alias
 */
export function getTitleForSlug(rawSlug) {
  const clean = extractSlug(rawSlug);
  
  if (!clean || clean === 'home' || clean === 'landing' || clean === 'index') {
    return DEFAULT_TITLE;
  }

  // Resolve internal navigation aliases (e.g. 'merchant' -> 'merchant-identities', 'domain' -> 'dns-management', 'league' -> 'node-infrastructure')
  let resolvedSlug = clean;
  if (typeof window !== 'undefined' && window.COSMO_ALIAS_MAP && window.COSMO_ALIAS_MAP[clean]) {
    resolvedSlug = window.COSMO_ALIAS_MAP[clean];
  } else if (ALIAS_MAP[clean]) {
    resolvedSlug = ALIAS_MAP[clean];
  }

  // Normalize in case alias contained .html or path
  resolvedSlug = extractSlug(resolvedSlug);

  const htmlKey = `${resolvedSlug}.html`;

  if (TITLE_MAP[htmlKey]) {
    return TITLE_MAP[htmlKey];
  }
  if (TITLE_MAP[resolvedSlug]) {
    return TITLE_MAP[resolvedSlug];
  }

  // Fallback: Check if template element has <title> in DOM
  if (typeof document !== 'undefined') {
    const templateEl = document.getElementById(`${resolvedSlug}-template`) || document.getElementById(resolvedSlug);
    if (templateEl) {
      const content = templateEl.textContent || templateEl.innerHTML;
      const match = content.match(/<title[^>]*>([^<]+)<\/title>/i);
      if (match && match[1]) {
        return match[1].trim();
      }
    }
  }

  // Fallback: Title case formatting (guaranteed clean without .html or ?query)
  const formatted = resolvedSlug
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
  return `${formatted} | Cosmo Crypt`;
}

const INACTIVE_TAB_TITLE = 'Enclave Protected — Return to Cosmo Crypt';
let currentActiveTitle = DEFAULT_TITLE;

// High-contrast Enterprise Security Enclave Badge (Gold Lock scaled to 75% inside the dark badge)
const INACTIVE_FAVICON = 'data:image/svg+xml,' + encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
  <rect width="24" height="24" rx="5" fill="#0A0E1A"/>
  <g transform="translate(3, 3) scale(0.75)">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" fill="#FBBF24"/>
  </g>
</svg>
`.trim());

let originalFaviconElements = [];

function captureOriginalFavicons() {
  if (typeof document === 'undefined') return;
  if (originalFaviconElements.length === 0) {
    const links = document.querySelectorAll("link[rel*='icon']");
    links.forEach(link => {
      originalFaviconElements.push({
        element: link,
        href: link.getAttribute('href') || link.href,
        type: link.getAttribute('type') || ''
      });
    });
  }
}

function setInactiveFavicon(isInactive) {
  if (typeof document === 'undefined') return;
  captureOriginalFavicons();

  if (isInactive) {
    if (originalFaviconElements.length === 0) {
      let iconLink = document.querySelector("link[rel*='icon']");
      if (!iconLink) {
        iconLink = document.createElement('link');
        iconLink.rel = 'icon';
        document.head.appendChild(iconLink);
        originalFaviconElements.push({ element: iconLink, href: '', type: '' });
      }
    }
    originalFaviconElements.forEach(({ element }) => {
      element.href = INACTIVE_FAVICON;
      element.type = 'image/svg+xml';
    });
  } else {
    originalFaviconElements.forEach(({ element, href, type }) => {
      element.href = href;
      if (type) {
        element.type = type;
      } else {
        element.removeAttribute('type');
      }
    });
  }
}

/**
 * Updates the document title based on explicit slug or current pathname
 * Supports both URL input and history state object.
 */
export function updateDocumentTitle(urlInput = null, stateInput = null) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const slugFromUrl = extractSlug(urlInput || window.location.pathname);

  let stateSlug = '';
  if (stateInput) {
    if (typeof stateInput === 'string') {
      stateSlug = extractSlug(stateInput);
    } else if (stateInput.spaView) {
      stateSlug = extractSlug(stateInput.spaView);
    }
  }

  // If URL resolves to root/empty ('/' or home) but state has a specific view (e.g. 'about'),
  // stateSlug takes precedence so root routes correctly display their subview title.
  const targetSlug = (slugFromUrl && slugFromUrl !== 'home' && slugFromUrl !== 'landing' && slugFromUrl !== 'index')
    ? slugFromUrl
    : (stateSlug || slugFromUrl || 'home');

  const resolvedTitle = getTitleForSlug(targetSlug);
  currentActiveTitle = resolvedTitle;

  // Only update immediately if this tab is currently active
  if (!document.hidden) {
    document.title = resolvedTitle;
  }
}

// Browser navigation hooks (client-side only)
if (typeof window !== 'undefined' && typeof history !== 'undefined') {
  // Capture favicons when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', captureOriginalFavicons);
  } else {
    captureOriginalFavicons();
  }

  // Intercept pushState & replaceState for seamless SPA router updates
  const originalPush = history.pushState;
  history.pushState = function (...args) {
    const result = originalPush.apply(this, args);
    const targetUrl = (typeof args[2] === 'string' && args[2]) ? args[2] : window.location.pathname;
    const stateObj = args[0];
    updateDocumentTitle(targetUrl, stateObj);
    return result;
  };

  const originalReplace = history.replaceState;
  history.replaceState = function (...args) {
    const result = originalReplace.apply(this, args);
    const targetUrl = (typeof args[2] === 'string' && args[2]) ? args[2] : window.location.pathname;
    const stateObj = args[0];
    updateDocumentTitle(targetUrl, stateObj);
    return result;
  };

  // Handle browser Back / Forward buttons
  window.addEventListener('popstate', (e) => {
    updateDocumentTitle(window.location.pathname, e.state);
  });

  // Handle Tab Switch (When user navigates to another browser tab)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      document.title = INACTIVE_TAB_TITLE;
      setInactiveFavicon(true);
    } else {
      document.title = currentActiveTitle;
      setInactiveFavicon(false);
    }
  });

  // Run immediately for initial page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => updateDocumentTitle());
  } else {
    updateDocumentTitle();
  }
}
